import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdvancedCanvasSettings } from "@/components/panels/advanced-canvas-settings-panel";
import { useUI } from "@/context/ui-context";
import { useDiagram } from "@/context/diagram-context";

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// Safe hook for UI context
const useSafeUI = () => {
  try {
    return useUI();
  } catch {
    return {
      canvasSettings: {
        showAttachmentPoints: false,
        backgroundColor: "#020C19",
        gridEnabled: true,
        snapToGrid: true,
        gridSize: 20,
        metadataLabel: {
          minSpacing: 200,
          minYSpacing: 17,
          smoothingAngle: (120 * Math.PI) / 180,
          stepSize: 20,
        },
        layerLabel: {
          width: 200,
          lineSpacing: 1.2,
          fontFamily: "sans-serif",
          fontSize: 60,
          fontWeight: "bold",
          hideLabels: false,
        },
      },
      setCanvasSettings: () => {},
      showAttachmentPoints: false,
      setShowAttachmentPoints: () => {},
    };
  }
};

// Safe hook for Diagram context
const useSafeDiagram = () => {
  try {
    return useDiagram();
  } catch {
    return null;
  }
};

const SettingsDialog: React.FC<SettingsDialogProps> = ({ isOpen, onClose }) => {
  const {
    canvasSettings,
    setCanvasSettings,
    showAttachmentPoints,
    setShowAttachmentPoints,
  } = useSafeUI();

  const diagram = useSafeDiagram();
  const canvasSize = diagram?.canvasSize || { width: 800, height: 600 };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-surface-border text-white w-[800px] max-w-[90vw] max-h-[90vh] overflow-auto">
        <DialogHeader className="px-6 py-4">
          <DialogTitle className="text-white">Display Settings</DialogTitle>
          <DialogDescription className="sr-only">
            Adjust the canvas and display settings.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6">
          <AdvancedCanvasSettings
            initialSettings={{
              canvas: {
                canvasSize,
                showAttachmentPoints,
              },
              metadataLabel: canvasSettings?.metadataLabel || {
                minSpacing: 200,
                minYSpacing: 17,
                smoothingAngle: (120 * Math.PI) / 180,
                stepSize: 20,
              },
              layerLabel: canvasSettings?.layerLabel || {
                width: 200,
                lineSpacing: 1.2,
                fontFamily: "sans-serif",
                fontSize: 60,
                fontWeight: "bold",
                hideLabels: true,
              },
            }}
            onSaveSettings={(settings) => {
              if (diagram && diagram.setCanvasSize) {
                diagram.setCanvasSize(settings.canvas.canvasSize);
              }
              if (setShowAttachmentPoints) {
                setShowAttachmentPoints(settings.canvas.showAttachmentPoints);
              }
              if (setCanvasSettings) {
                setCanvasSettings(settings);
              }
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;
